/**
 * `111-frontend-architecture.md` §8.1 — three levels, so that one broken pane never
 * takes the app down. The pane-level boundary is the one that earns its keep: a crash
 * in the backlinks panel must not destroy the editor the user is typing into.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { translate } from '@/lib/i18n';

interface Props {
  children: ReactNode;
  /** "root" gets the full-page screen; "pane" gets an inline card inside that pane. */
  level?: 'root' | 'pane';
  label?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', this.props.label ?? this.props.level ?? 'root', error, info);
  }

  private readonly reset = () => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    if (this.props.level === 'pane') {
      return (
        <div className="m-2 rounded-md border border-[var(--destructive)] p-3">
          <p className="font-medium">{translate('shell.error.paneTitle')}</p>
          <p className="mb-2 text-[var(--muted-foreground)]">{error.message}</p>
          <Button variant="outline" size="sm" onClick={this.reset}>
            {translate('shell.error.retry')}
          </Button>
        </div>
      );
    }

    // 110 §8: "Users report bugs badly; give them something to paste."
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8">
        <div className="w-full max-w-[560px] space-y-2">
          <h1 className="text-lg font-semibold">{translate('shell.error.title')}</h1>
          <p className="text-[var(--muted-foreground)]">{translate('shell.error.body')}</p>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                window.location.reload();
              }}
            >
              {translate('shell.error.reload')}
            </Button>
            <Button variant="outline" onClick={this.reset}>
              {translate('shell.error.tryAgain')}
            </Button>
          </div>
          <details className="mt-2">
            <summary className="cursor-pointer text-[var(--muted-foreground)]">
              {translate('shell.error.technicalDetails')}
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-[var(--muted)] p-2 text-xs">
              {error.stack ?? error.message}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
